
import { useMemo } from "react";
import { TikTokVideo } from "@/types/api";
import { useVideoDownloader } from "./useVideoDownloader";
import { useDownloadControls } from "./useDownloadControls";
import { getDownloadableVideos, createDownloadOrderMap, calculateProgressCounts } from "@/utils/downloadUtils";

export type DownloadStatus = "not_started" | "in_progress" | "complete" | "failed" | "paused";

export interface ProgressState {
  [videoId: string]: {
    status: DownloadStatus;
    retries: number;
    error?: string;
    blob?: Blob;
    startTime?: number;
    endTime?: number;
  };
}

export function useBulkDownload(videos: TikTokVideo[], selected: string[]) {
  const downloadList = useMemo(() => getDownloadableVideos(videos, selected), [videos, selected]);
  const downloadOrderMap = useMemo(() => createDownloadOrderMap(downloadList), [downloadList]);

  const {
    progressState,
    setProgressState,
    downloadControllers,
    downloadSingle
  } = useVideoDownloader();

  const {
    pausedVideos,
    setPausedVideos,
    isBulkDownloading,
    setIsBulkDownloading,
    cancelAll,
    setCancelAll,
    handlePauseOne,
    handleResumeOne,
    handlePauseAll,
    handleResumeAll,
    handleCancelAll,
    handleRetryOne,
    handleDownloadZip
  } = useDownloadControls(downloadList, progressState, setProgressState, downloadControllers);

  const progressCounts = useMemo(
    () => calculateProgressCounts(downloadList, progressState),
    [downloadList, progressState]
  );

  const processNextVideoSequentially = (idx: number) => {
    if (cancelAll || idx >= downloadList.length) {
      setIsBulkDownloading(false);
      return;
    }
    const video = downloadList[idx];
    if (pausedVideos.includes(video.id) || video.status === "unavailable") {
      setProgressState(state => ({
        ...state,
        [video.id]: {
          ...(state[video.id] || {}),
          status: video.status === "unavailable" ? "failed" : "paused",
          error: video.status === "unavailable" ? "Unavailable from TikTok" : undefined,
          retries: state[video.id]?.retries || 0,
        }
      }));
      processNextVideoSequentially(idx + 1);
      return;
    }
    downloadSingle(video, 0, idx, processNextVideoSequentially, cancelAll, pausedVideos);
  };

  const handleBulkDownload = () => {
    setIsBulkDownloading(true);
    setCancelAll(false);
    setPausedVideos([]);
    setProgressState(Object.fromEntries(
      downloadList.map(v => [v.id, { status: "not_started", retries: 0 }])
    ));
    processNextVideoSequentially(0);
  };

  const handleRetryFailedBulk = () => {
    downloadList
      .filter((v) => progressState[v.id]?.status === "failed")
      .forEach((v) => handleRetryOne(v.id));
    if (!isBulkDownloading) {
      const firstIdx = downloadList.findIndex(v =>
        progressState[v.id]?.status === "not_started" ||
        progressState[v.id]?.status === "failed"
      );
      if (firstIdx !== -1) {
        setIsBulkDownloading(true);
        setCancelAll(false);
        processNextVideoSequentially(firstIdx);
      }
    }
  };

  return {
    progressState,
    isBulkDownloading,
    handleBulkDownload,
    handlePauseAll,
    handleResumeAll,
    handleCancelAll,
    handleRetryFailedBulk,
    handleRetryOne,
    handleResumeOne,
    handlePauseOne,
    handleDownloadZip,
    pausedVideos,
    downloadList,
    progressCounts,
    downloadOrderMap,
  };
}
